import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string): string {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(buildUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    let msg = payload?.error?.message || payload?.message || res.statusText || 'Request failed'
    if (typeof msg !== 'string') msg = 'Request failed'
    throw new Error(msg)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

// ─── Types ─────────────────────────────────────────────────────────────────

export interface LeadData {
  id: string
  name?: string
  email?: string
  company?: string
  phone?: string
  hubspotObjectType: 'contact' | 'lead'
}

export interface QualificationResultData {
  status: 'qualified' | 'unqualified' | 'pending'
  score: number
  summary: string
}

// Fired when the rep's mailbox can't send — every active campaign of theirs
// is stalling until they reconnect (ZAI-387). Rep-level, so it carries no
// campaign/recipient reference.
export interface CampaignPauseData {
  provider: 'gmail'
}

export interface NotificationRecord {
  id: string
  workspaceId: string
  userId: string
  type: 'LEAD_ASSIGNED' | 'PROSPECT_QUALIFICATION_RESULT' | 'CAMPAIGN_SEND_PAUSED'
  status: 'unread' | 'dismissed'
  lead?: LeadData
  qualification?: QualificationResultData
  campaignPause?: CampaignPauseData
  idempotencyKey: string
  createdAt: string
  updatedAt: string
}

// ─── API calls ─────────────────────────────────────────────────────────────

export function listNotifications(workspaceId: string): Promise<{ data: NotificationRecord[] }> {
  return request('GET', `api/workspaces/${workspaceId}/notifications`)
}

export function dismissNotification(workspaceId: string, notificationId: string): Promise<{ data: NotificationRecord }> {
  return request('POST', `api/workspaces/${workspaceId}/notifications/${notificationId}/dismiss`)
}

export function markNotificationRead(workspaceId: string, notificationId: string): Promise<{ data: NotificationRecord }> {
  return request('POST', `api/workspaces/${workspaceId}/notifications/${notificationId}/read`)
}
